import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format a US phone number for display as (786) 678-5200.
 * Accepts any input (digits, +1 prefix, existing formatting). If the value
 * is not a recognizable 10-digit US number it is returned unchanged so we
 * never hide or corrupt non-standard / international data.
 */
export function formatPhone(value) {
  if (value === null || value === undefined) return value;
  const str = String(value).trim();
  if (!str) return str;
  // Preserve an optional leading '+' (international) that we won't touch below.
  const digits = str.replace(/\D/g, "");
  let national = digits;
  if (national.length === 11 && national.startsWith("1")) {
    national = national.slice(1);
  }
  if (national.length !== 10) {
    return str; // not a standard US 10-digit number -> leave as-is
  }
  const area = national.slice(0, 3);
  const prefix = national.slice(3, 6);
  const line = national.slice(6);
  return `(${area}) ${prefix}-${line}`;
}

/**
 * Progressive formatter for phone <input> onChange handlers.
 * Returns the value the user should see while typing, e.g.:
 *   "786" -> "(786"   "7866" -> "(786) 6"   "7866785200" -> "(786) 678-5200"
 * Caps at 10 national digits (strips a leading US country code).
 */
export function formatPhoneInput(value) {
  if (value === null || value === undefined) return "";
  let digits = String(value).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}


/**
 * Safely extract error message from API response
 * Handles various error formats including Pydantic validation errors
 */
export function getErrorMessage(error, fallback = 'An error occurred') {
  // If no error or no response, return fallback
  if (!error?.response?.data) {
    return error?.message || fallback;
  }

  const data = error.response.data;

  // If data is a string, return it directly
  if (typeof data === 'string') {
    return data;
  }

  // If data has a detail property
  if (data.detail) {
    // detail might be a string
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    // detail might be an array (Pydantic validation errors)
    if (Array.isArray(data.detail)) {
      return data.detail
        .map(err => {
          if (typeof err === 'string') return err;
          // Pydantic error format: { type, loc, msg, input, ctx, url }
          if (err.msg) {
            const field = err.loc?.slice(-1)[0] || 'field';
            return `${field}: ${err.msg}`;
          }
          return JSON.stringify(err);
        })
        .join(', ');
    }
    // detail is an object with a msg property
    if (data.detail.msg) {
      return data.detail.msg;
    }
    // detail is some other object
    return JSON.stringify(data.detail);
  }

  // Check for other common error properties
  if (data.msg) return data.msg;
  if (data.message) return data.message;
  if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);

  // Fallback to stringifying the entire data object
  try {
    return JSON.stringify(data);
  } catch {
    return fallback;
  }
}

// Format date - handles date-only strings (YYYY-MM-DD) correctly to avoid timezone issues
export function formatDate(dateString) {
  if (!dateString) return '';
  // If it's a date-only string (YYYY-MM-DD), parse it directly without timezone conversion
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  // Otherwise, parse as full datetime
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Format date with time
export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Get status color class
export function getStatusClass(status) {
  const statusMap = {
    // Contract pipeline — each status a distinct hue for fast scanning.
    // Kept consistent across badges, the contracts table and the timeline.
    'Submitted': 'bg-slate-100 text-slate-700',
    'Requested': 'bg-slate-100 text-slate-700',
    'In_Progress': 'bg-amber-100 text-amber-800',
    'Submitted_To_Carrier': 'bg-indigo-100 text-indigo-800',
    'Carrier_Pending': 'bg-sky-100 text-sky-800',
    'Pending_Carrier': 'bg-sky-100 text-sky-800',
    'Missing_Information': 'bg-yellow-100 text-yellow-800',
    'Missing_Info': 'bg-yellow-100 text-yellow-800',
    'Invitation_Sent_To_Agent': 'bg-violet-100 text-violet-800',
    'Waiting_On_Agent': 'bg-violet-100 text-violet-800',
    'Pending_Release': 'bg-orange-100 text-orange-900 ring-1 ring-orange-300',
    'On_Hold': 'bg-rose-100 text-rose-800',
    'Approved': 'bg-emerald-100 text-emerald-800',
    'Active': 'bg-emerald-100 text-emerald-800',
    'Declined': 'bg-red-100 text-red-800',
    'Terminated': 'bg-gray-200 text-gray-700',
    'Closed': 'bg-stone-200 text-stone-700',
    'Archived': 'bg-gray-100 text-gray-500',
    // Ticket statuses
    'Open': 'bg-blue-100 text-blue-800',
    'Waiting_User': 'bg-orange-100 text-orange-800',
    // Agent statuses
    'active': 'bg-emerald-100 text-emerald-800',
    'inactive': 'bg-gray-100 text-gray-800',
    'pending': 'bg-amber-100 text-amber-800',
    'onboarding': 'bg-blue-100 text-blue-800',
    // Invitation statuses
    'Pending': 'bg-amber-100 text-amber-800',
    'Accepted': 'bg-emerald-100 text-emerald-800',
    'Expired': 'bg-gray-100 text-gray-800',
    // Compliance statuses
    'Complete': 'bg-emerald-100 text-emerald-800',
    'Missing': 'bg-red-100 text-red-800',
  };
  return statusMap[status] || 'bg-slate-100 text-slate-800';
}

// Format status label
export function formatStatus(status) {
  if (!status) return '';
  return status.replace(/_/g, ' ');
}

// Get user initials
export function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

// Get role display name
export function getRoleDisplayName(role) {
  const roleMap = {
    'KR_SuperAdmin': 'KR Super Admin',
    'KR_AdminOps': 'KR Admin Ops',
    'Accounting': 'Accounting',
    'Agency_Principal': 'Agency Principal',
    'Agency_User': 'Agency User',
    'Agent': 'Agent',
  };
  return roleMap[role] || role;
}

// Check if user is KR admin (context-aware)
export function isKRRole(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.type === 'kr_admin';
  if (!user?.roles) return false;
  return user.roles.some(r => r.role === 'KR_SuperAdmin' || r.role === 'KR_AdminOps');
}

// Check if user is KR SuperAdmin (context-aware)
export function isKRSuperAdmin(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.type === 'kr_admin' && ctx.role === 'KR_SuperAdmin';
  if (!user?.roles) return false;
  return user.roles.some(r => r.role === 'KR_SuperAdmin');
}

// Check if user is Agency Principal (context-aware)
export function isAgencyPrincipal(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.type === 'agency' && ctx.role === 'Agency_Principal';
  if (!user?.roles) return false;
  return user.roles.some(r => r.role === 'Agency_Principal');
}

// Check if user is Agent (context-aware)
export function isAgent(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.type === 'agent';
  return !!user?.agent_npn;
}

// Check if user has Accounting role
export function isAccountingRole(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.role === 'Accounting';
  if (!user?.roles) return false;
  return user.roles.some(r => r.role === 'Accounting');
}

// Check if user is in agency context (either principal or user)
export function isAgencyContext(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.type === 'agency';
  if (!user?.roles) return false;
  return user.roles.some(r => r.role === 'Agency_Principal' || r.role === 'Agency_User');
}

// Phase 26.8.1 — who can create events. KR staff always; Agency
// Principal or Agency Admin (Agency_User) when acting inside an agency
// context. Agents and unauthenticated users may not create events.
export function canCreateEvents(user) {
  if (isKRRole(user)) return true;
  const ctx = user?.active_context;
  if (ctx?.type === 'agency' && (ctx.role === 'Agency_Principal' || ctx.role === 'Agency_User')) {
    return true;
  }
  if (!ctx) {
    return (user?.roles || []).some(
      r => r.role === 'Agency_Principal' || r.role === 'Agency_User',
    );
  }
  return false;
}

// Get user's primary role (context-aware)
export function getPrimaryRole(user) {
  const ctx = user?.active_context;
  if (ctx) return ctx.role;
  if (!user?.roles?.length) {
    if (user?.agent_npn) return 'Agent';
    return 'Unknown';
  }
  const priority = ['KR_SuperAdmin', 'KR_AdminOps', 'Accounting', 'Agency_Principal', 'Agency_User'];
  for (const role of priority) {
    if (user.roles.some(r => r.role === role)) return role;
  }
  return user.roles[0].role;
}

// Get user's agency ID (context-aware)
export function getUserAgencyId(user) {
  const ctx = user?.active_context;
  if (ctx) {
    if (ctx.type === 'agency') return ctx.id;
    return null;
  }
  if (!user?.roles?.length) return null;
  const agencyRole = user.roles.find(r => r.agency_id);
  return agencyRole?.agency_id || null;
}

// Build agency tree from flat list
export function buildAgencyTree(agencies) {
  const map = {};
  const roots = [];
  
  // Create map
  agencies.forEach(agency => {
    map[agency.agency_id] = { ...agency, children: [] };
  });
  
  // Build tree
  agencies.forEach(agency => {
    if (agency.parent_agency_id && map[agency.parent_agency_id]) {
      map[agency.parent_agency_id].children.push(map[agency.agency_id]);
    } else {
      roots.push(map[agency.agency_id]);
    }
  });
  
  return roots;
}

// Flatten agency tree for dropdown
export function flattenAgencyTree(tree, depth = 0, result = []) {
  tree.forEach(node => {
    result.push({ ...node, depth, children: undefined });
    if (node.children?.length) {
      flattenAgencyTree(node.children, depth + 1, result);
    }
  });
  return result;
}

// Contract status workflow order
export const CONTRACT_STATUS_ORDER = [
  'Submitted',
  'In_Progress',
  'Carrier_Pending',
  'Waiting_On_Agent',
  'Missing_Information',
  'Pending_Release',
  'Approved',
  'Declined',
  'On_Hold',
  'Terminated',
  'Closed',
];

// Ticket categories
export const TICKET_CATEGORIES = [
  { value: 'Contracting', label: 'Contracting' },
  { value: 'Certification', label: 'Certification' },
  { value: 'Carrier_Issue', label: 'Carrier Issue' },
  { value: 'Technical_Support', label: 'Technical Support' },
  { value: 'General_Question', label: 'General Question' },
  { value: 'Contract', label: 'Contract' },
  { value: 'Level/Hierarchy', label: 'Level / Hierarchy' },
  { value: 'Tech', label: 'Technical' },
  { value: 'Other', label: 'Other' },
];

export const TICKET_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-slate-500' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600' },
  { value: 'high', label: 'High', color: 'text-red-600' },
];

export const PRODUCT_LINES = [
  { value: 'ACA', label: 'ACA' },
  { value: 'Medicare', label: 'Medicare' },
  { value: 'Life_Annuity', label: 'Life & Annuity' },
  { value: 'Ancillary', label: 'Ancillary' },
  { value: 'Private_Health_Plans', label: 'Private Health Plans' },
];

// US States
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// Termination reasons
export const TERMINATION_REASONS = [
  { value: 'agent_request', label: 'Agent Request' },
  { value: 'carrier_termination', label: 'Carrier Termination' },
  { value: 'compliance', label: 'Non-Compliance' },
  { value: 'debt', label: 'Debt' },
  { value: 'no_cause', label: 'No Cause' },
  { value: 'other', label: 'Other' },
];

// Appointment statuses
export const APPOINTMENT_STATUSES = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Declined', label: 'Declined' },
  { value: 'Terminated', label: 'Terminated' },
];
